<script setup lang="ts">
/** Bay widths (grid family, `perBay` parameter): clear width of each bay, left to right; the neighbour adjusts. */
import { computed } from 'vue';
import type { ParameterDef } from '@/families/types';
import { bayWidthsOf, fmtMm, mm, parseMm, sum } from '@/domain';
import Icon from './Icon.vue';
import { snap, ui } from '@/app/state';
import { commitBayWidth, evenBays } from '@/app/edits';
import { focusSoon } from '@/app/navigation';

defineProps<{ p: ParameterDef }>();
const widths = computed(() => { void snap.value; return bayWidthsOf(snap.value.design, snap.value.stock); });
const equal = computed(() => widths.value.every((w) => Math.abs(w - widths.value[0]!) <= 1));
const rows = computed(() => widths.value.map((w, i) => {
  const id = `bw-${i}`;
  const key = `bayWidth-${i}`;
  return { i, id, key, err: ui.fieldErrors[key] ?? null, value: id in ui.drafts ? ui.drafts[id]! : fmtMm(w) };
}));
function onInput(e: Event) { const t = e.target as HTMLInputElement; ui.drafts[t.id] = t.value; }
function commit(i: number, raw: string) { commitBayWidth(i, raw, `bw-${i}`); }
function onKey(e: KeyboardEvent, i: number) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commit(i, el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const next = fmtMm(r.ticks + mm((e.shiftKey ? 10 : 1) * (e.key === 'ArrowUp' ? 1 : -1)));
  ui.drafts[el.id] = next;
  el.value = next;
  commit(i, next);
}
function makeEqual() { focusSoon('[data-act="bays-even"]'); evenBays(); }
</script>

<template>
  <div class="bay-widths">
    <div class="sec-head"><h3 id="bwH" class="h-sub">{{ p.ui.label }} <span class="muted">clear, left to right</span></h3>
      <button type="button" class="btn btn--sm btn--quiet" data-act="bays-even" :disabled="equal" @click="makeEqual">Equal widths</button>
    </div>
    <ol class="ops ops--bays" aria-labelledby="bwH">
      <li v-for="r in rows" :key="r.i" class="op" :class="{ 'has-err': !!r.err, 'is-sel': ui.bay === r.i }" :data-bay="r.i">
        <label class="op-l" :for="r.id"><span class="op-n" aria-hidden="true">{{ r.i + 1 }}</span><span>Bay {{ r.i + 1 }}</span></label>
        <div class="input-unit input-unit--sm">
          <input :id="r.id" class="input mono" inputmode="decimal" autocomplete="off" :value="r.value" :aria-invalid="r.err ? 'true' : undefined" :aria-describedby="r.err ? `err-${r.key}` : undefined" @input="onInput" @change="($event.target as HTMLInputElement).isConnected && commit(r.i, ($event.target as HTMLInputElement).value)" @keydown="onKey($event, r.i)" @focus="ui.bay = r.i"><span>mm</span>
        </div>
        <p v-if="r.err" :id="`err-${r.key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ r.err }}</span></p>
      </li>
    </ol>
    <p class="hint">{{ p.ui.help }} Total {{ fmtMm(sum(widths)) }} mm.</p>
  </div>
</template>
